import React, { useState, useEffect } from "react";
import { 
  makeStyles, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import SaveIcon from '@mui/icons-material/Save';
import UpdateIcon from '@mui/icons-material/Update';
import AssessmentIcon from '@mui/icons-material/Assessment';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
  },
  card: {
    padding: theme.spacing(4),
    borderRadius: "24px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${theme.palette.divider}`,
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  titleIcon: {
    fontSize: "2rem",
    color: "#6517ab",
  },
  fieldInfo: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
    lineHeight: 1.5,
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      backgroundColor: theme.palette.type === 'light' ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
    },
  },
  saveButton: {
    height: "52px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #6517ab 0%, #8e44ad 100%)",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    marginTop: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 24px rgba(101, 23, 171, 0.4)",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(5),
  },
  vInfo: {
    padding: theme.spacing(2),
    borderRadius: "16px",
    backgroundColor: "rgba(101, 23, 171, 0.05)",
    border: "1px dashed rgba(101, 23, 171, 0.3)",
    marginBottom: theme.spacing(3),
  }
}));

const VersionManager = () => {
  const classes = useStyles();
  const [version, setVersion] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const { data } = await api.get("/version");
        setVersion(data.version || "");
      } catch (err) {
        toast.error("Erro ao carregar a versão do sistema.");
      } finally {
        setLoading(false);
      }
    };
    fetchVersion();
  }, []);

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await api.post("/version", { version });
      toast.success("Versão do sistema atualizada com sucesso!");
    } catch (err) {
      toast.error("Falha ao atualizar a versão do sistema.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Grid container className={classes.container} spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper className={classes.card} elevation={0}>
          <Box className={classes.titleContainer}>
            <UpdateIcon className={classes.titleIcon} />
            <Typography variant="h5" style={{ fontWeight: 800 }}>
              Controle de Versão
            </Typography>
          </Box>

          <Box className={classes.vInfo}>
            <Typography style={{ fontWeight: 600, color: "#6517ab", fontSize: "0.9rem" }}>
              Dica Pro:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              A versão definida aqui será exibida nas páginas públicas de Login e Recuperação de Senha, permitindo um controle dinâmico sem tocar no código.
            </Typography>
          </Box>

          <Typography className={classes.fieldInfo}>
            Insira o identificador da versão abaixo (Ex: 1.5.2 Pro ou Orion v2.0):
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            label="Versão do Frontend"
            className={classes.textField}
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AssessmentIcon style={{ color: "#6517ab" }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            className={classes.saveButton}
            onClick={handleUpdate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {saving ? "Salvando..." : "Salvar Nova Versão"}
          </Button>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Box p={3} style={{ borderLeft: "4px solid rgba(101, 23, 171, 0.2)" }}>
          <Typography variant="h6" gutterBottom style={{ fontWeight: 700 }}>
            Sobre o Gerenciamento Dinâmico
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Ao alterar a versão por esta interface, você garante que todos os usuários vejam a versão correta do sistema imediatamente, sem a necessidade de novos deploys.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta funcionalidade consome diretamente o endpoint do backend e persiste os dados na tabela global de versões. Ideal para ambientes Multi-tenant e plataformas Whitelabel.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

export default VersionManager;
